import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabase';
import { TranscriptionJob } from '../types';
import { UsageProgress } from '../components/UsageProgress';
import { Video, Clock, Calendar, Search, Plus, ChevronRight, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transcriptions, setTranscriptions] = useState<TranscriptionJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    totalVideos: 0,
    totalMinutes: 0,
    avgProcessingTime: 0
  });

  useEffect(() => {
    if (user) {
      fetchTranscriptions();
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    if (!user) return;

    try {
      // Get total videos and minutes from transcription_jobs
      const { data: transcriptionStats, error: transcriptionError } = await supabase
        .from('transcription_jobs')
        .select('duration_seconds, processing_duration_seconds')
        .eq('user_id', user.id);

      if (transcriptionError) throw transcriptionError;

      // Calculate stats
      const totalVideos = transcriptionStats?.length || 0;
      const totalMinutes = transcriptionStats?.reduce((sum, job) => 
        sum + (job.duration_seconds ? Math.ceil(job.duration_seconds / 60) : 0), 0) || 0;

      // Calculate average processing time
      const completedJobs = transcriptionStats?.filter(job => job.processing_duration_seconds);
      const avgProcessingTime = completedJobs?.length 
        ? Math.round(completedJobs.reduce((sum, job) => sum + (job.processing_duration_seconds || 0), 0) / completedJobs.length / 60)
        : 0;

      setStats({
        totalVideos,
        totalMinutes,
        avgProcessingTime
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load usage statistics');
    }
  };

  const fetchTranscriptions = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('transcription_jobs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTranscriptions(data || []);
    } catch (error) {
      console.error('Error fetching transcriptions:', error);
      toast.error('Failed to load transcriptions');
    } finally {
      setLoading(false);
    }
  };

  const filteredTranscriptions = transcriptions.filter(job =>
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.transcript.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your transcriptions and monitor your usage
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-card rounded-xl shadow-sm p-6 border hover:border-primary/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Video className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Videos Processed</p>
                <p className="text-2xl font-semibold text-foreground">{stats.totalVideos}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow-sm p-6 border hover:border-primary/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Minutes Processed</p>
                <p className="text-2xl font-semibold text-foreground">{stats.totalMinutes}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow-sm p-6 border hover:border-primary/50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg. Processing Time</p>
                <p className="text-2xl font-semibold text-foreground">{stats.avgProcessingTime} min</p>
              </div>
            </div>
          </div>
        </div>

        {/* Transcriptions Section */}
        <div className="bg-card rounded-xl shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-lg font-semibold text-foreground">Recent Transcriptions</h2>
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search transcriptions..."
                    className="w-full sm:w-64 pl-10 pr-4 py-2 border bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <button
                  onClick={() => navigate('/tool')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>New</span>
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
              <p className="mt-4 text-muted-foreground">Loading transcriptions...</p>
            </div>
          ) : filteredTranscriptions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredTranscriptions.map((job) => (
                <div
                  key={job.id}
                  onClick={() => navigate(`/transcription/${job.id}`)}
                  className="group bg-card rounded-lg border hover:border-primary hover:shadow-md transition-all cursor-pointer p-6"
                >
                  <h3 className="font-medium text-foreground mb-2 line-clamp-2">{job.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{job.transcript}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{new Date(job.created_at).toLocaleDateString()}</span>
                    <div className="flex items-center gap-2">
                      <span>{Math.round((job.duration_seconds || 0) / 60)} min</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">
                {searchQuery
                  ? 'No transcriptions found matching your search'
                  : 'No transcriptions yet. Create your first one!'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}